"use client";

import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Pencil, Trash2, IndianRupee, Loader2 } from "lucide-react";
import * as teacherSalaryService from "../../../services/teacherSalaryService";
import * as staffSalaryService from "../../../services/staffSalaryService";
import { API_ENDPOINTS } from "../../../constants/config";
import { getFullName } from "../../../utils/urlUtils";
import axiosInstance from "../../../utils/axiosInstance";
import { toast } from "react-hot-toast";

const defaultForm = {
  teacherId: "",
  basicSalary: "",
  effectiveFrom: "",
  effectiveTo: "",
  allowances: "0",
  fixedDeductions: "0",
  remarks: "",
  allowanceEntries: {},
  deductionEntries: {},
};

export default function SalaryConfigContent({ employeeType: employeeTypeProp } = {}) {
  const outletContext = useOutletContext();
  const employeeType = employeeTypeProp || outletContext?.employeeType || "teacher";
  const isStaff = employeeType === "staff";
  const employeeLabel = isStaff ? "Staff" : "Teacher";
  const [configs, setConfigs] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allowanceHeads, setAllowanceHeads] = useState([]);
  const [deductionHeads, setDeductionHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });

  const loadHeads = async () => {
    try {
      const res = await axiosInstance.get("/allowance-deduction-heads");
      const list = res.data?.data || [];
      setAllowanceHeads(list.filter((h) => h.type === "allowance"));
      setDeductionHeads(list.filter((h) => h.type === "deduction"));
    } catch (e) {
      setAllowanceHeads([]);
      setDeductionHeads([]);
    }
  };

  const loadConfigs = async () => {
    try {
      const service = isStaff ? staffSalaryService : teacherSalaryService;
      const res = await service.fetchSalaryConfigs();
      if (res.success) setConfigs(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load salary configs");
      setConfigs([]);
    }
  };

  const loadTeachers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(isStaff ? API_ENDPOINTS.usersStaff : API_ENDPOINTS.usersTeachers, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.status === "success" && (data.data?.teachers || data.data?.staff)) {
        setTeachers(isStaff ? data.data.staff : data.data.teachers);
      }
    } catch (e) {
      console.error("Failed to load employees", e);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadConfigs(), loadTeachers(), loadHeads()]);
      setLoading(false);
    })();
  }, [employeeType, isStaff]);

  const openCreate = () => {
    setEditingId(null);
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const aEntries = {};
    allowanceHeads.forEach((h) => { aEntries[h._id] = ""; });
    const dEntries = {};
    deductionHeads.forEach((h) => { dEntries[h._id] = ""; });
    setForm({
      ...defaultForm,
      effectiveFrom: nextMonth.toISOString().slice(0, 10),
      allowanceEntries: aEntries,
      deductionEntries: dEntries,
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    const employee = isStaff ? row.staff : row.teacher;
    const teacherId = typeof employee === "object" ? employee._id : employee;
    const aEntries = {};
    allowanceHeads.forEach((h) => { aEntries[h._id] = ""; });
    const dEntries = {};
    deductionHeads.forEach((h) => { dEntries[h._id] = ""; });
    if (row.allowanceHeadEntries?.length) {
      row.allowanceHeadEntries.forEach((e) => {
        const headId = typeof e.head === "object" ? e.head?._id : e.head;
        if (headId) aEntries[headId] = e.valueOverride != null ? String(e.valueOverride) : "";
      });
    }
    if (row.deductionHeadEntries?.length) {
      row.deductionHeadEntries.forEach((e) => {
        const headId = typeof e.head === "object" ? e.head?._id : e.head;
        if (headId) dEntries[headId] = e.valueOverride != null ? String(e.valueOverride) : "";
      });
    }
    setForm({
      teacherId,
      basicSalary: String(row.basicSalary),
      effectiveFrom: row.effectiveFrom ? new Date(row.effectiveFrom).toISOString().slice(0, 10) : "",
      effectiveTo: row.effectiveTo ? new Date(row.effectiveTo).toISOString().slice(0, 10) : "",
      allowances: String(row.allowances ?? 0),
      fixedDeductions: String(row.fixedDeductions ?? 0),
      remarks: row.remarks || "",
      allowanceEntries: aEntries,
      deductionEntries: dEntries,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.teacherId || !form.basicSalary || !form.effectiveFrom) {
      toast.error(`${employeeLabel}, basic salary and effective from are required`);
      return;
    }
    setSubmitLoading(true);
    setMessage({ type: "", content: "" });
    try {
      const allowanceHeadEntries =
        allowanceHeads.length > 0
          ? allowanceHeads.map((h) => ({
              headId: h._id,
              valueOverride:
                form.allowanceEntries[h._id] === "" || form.allowanceEntries[h._id] == null
                  ? null
                  : Number(form.allowanceEntries[h._id]),
            }))
          : undefined;
      const deductionHeadEntries =
        deductionHeads.length > 0
          ? deductionHeads.map((h) => ({
              headId: h._id,
              valueOverride:
                form.deductionEntries[h._id] === "" || form.deductionEntries[h._id] == null
                  ? null
                  : Number(form.deductionEntries[h._id]),
            }))
          : undefined;
      const payload = {
        ...(isStaff ? { staffId: form.teacherId } : { teacherId: form.teacherId }),
        basicSalary: Number(form.basicSalary),
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo?.trim() || null,
        allowances: allowanceHeads.length === 0 ? Number(form.allowances) || 0 : 0,
        fixedDeductions: deductionHeads.length === 0 ? Number(form.fixedDeductions) || 0 : 0,
        remarks: form.remarks?.trim() || "",
      };
      if (allowanceHeadEntries !== undefined) payload.allowanceHeadEntries = allowanceHeadEntries;
      if (deductionHeadEntries !== undefined) payload.deductionHeadEntries = deductionHeadEntries;
      const service = isStaff ? staffSalaryService : teacherSalaryService;
      if (editingId) {
        await service.updateSalaryConfig(editingId, payload);
        toast.success("Salary config updated");
      } else {
        await service.createSalaryConfig(payload);
        toast.success("Salary config created");
      }
      setDialogOpen(false);
      loadConfigs();
    } catch (e) {
      setMessage({
        type: "error",
        content: e.response?.data?.message || "Failed to save",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const [toDelete, setToDelete] = useState(null);
  const handleDelete = async () => {
    if (!toDelete) return;
    setSubmitLoading(true);
    try {
      const service = isStaff ? staffSalaryService : teacherSalaryService;
      await service.deleteSalaryConfig(toDelete._id);
      toast.success("Salary config deleted");
      setDeleteDialogOpen(false);
      setToDelete(null);
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete");
    } finally {
      setSubmitLoading(false);
    }
  };

  const formatMoney = (n) => (n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
              <IndianRupee className="h-5 w-5 text-[#0263cc]" />
              {employeeLabel} Salary Config
            </CardTitle>
            <Button
              onClick={openCreate}
              className="bg-[#0263cc] hover:bg-[#0255b0] text-white shrink-0"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Salary
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Set basic salary and effective date per {employeeLabel.toLowerCase()} for payroll calculation.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold">Employee ID</TableHead>
                  <TableHead className="font-semibold">Basic Salary</TableHead>
                  <TableHead className="font-semibold">Allowances</TableHead>
                  <TableHead className="font-semibold">Deductions</TableHead>
                  <TableHead className="font-semibold">Effective From</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                      No salary configs yet. Add one to run payroll.
                    </TableCell>
                  </TableRow>
                ) : (
                  configs.map((row) => {
                    const employee = isStaff ? row.staff : row.teacher;
                    const name = employee ? getFullName(employee) : "—";
                    const empCode = isStaff ? employee?.staffId ?? "—" : employee?.employeeCode ?? "—";
                    const allowancesDisplay = row.effectiveAllowances != null ? row.effectiveAllowances : row.allowances;
                    const deductionsDisplay = row.effectiveDeductions != null ? row.effectiveDeductions : row.fixedDeductions;
                    return (
                      <TableRow key={row._id} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell>{empCode}</TableCell>
                        <TableCell>{formatMoney(row.basicSalary)}</TableCell>
                        <TableCell>{formatMoney(allowancesDisplay)}</TableCell>
                        <TableCell>{formatMoney(deductionsDisplay)}</TableCell>
                        <TableCell>
                          {row.effectiveFrom ? new Date(row.effectiveFrom).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(row)} className="text-slate-600">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setToDelete(row); setDeleteDialogOpen(true); }} className="text-red-600 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc]">{editingId ? "Edit Salary Config" : "Add Salary Config"}</DialogTitle>
            <DialogDescription>Set basic salary and effective date for the {employeeLabel.toLowerCase()}.</DialogDescription>
          </DialogHeader>
          {message.content && (
            <Alert variant={message.type === "error" ? "destructive" : "default"}>
              <AlertDescription>{message.content}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>{employeeLabel} <span className="text-red-500">*</span></Label>
              <Select
                value={form.teacherId}
                onValueChange={(v) => setForm((f) => ({ ...f, teacherId: v }))}
                disabled={!!editingId}
              >
                <SelectTrigger><SelectValue placeholder={`Select ${employeeLabel.toLowerCase()}`} /></SelectTrigger>
                <SelectContent>
                  {teachers.map((t) => (
                    <SelectItem key={t._id} value={t._id}>
                      {getFullName(t)} {isStaff ? (t.staffId ? `(${t.staffId})` : "") : (t.employeeCode ? `(${t.employeeCode})` : "")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Basic Salary (₹) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  min={0}
                  value={form.basicSalary}
                  onChange={(e) => setForm((f) => ({ ...f, basicSalary: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Effective From <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={form.effectiveFrom}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective To (optional)</Label>
                <Input
                  type="date"
                  value={form.effectiveTo || ""}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveTo: e.target.value }))}
                  placeholder="Leave empty for open-ended"
                />
              </div>
            </div>
            {allowanceHeads.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-[#0263cc]">Allowances (from created heads)</Label>
                <p className="text-xs text-slate-500">Leave amount blank to use head default (fixed or % of basic).</p>
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2 bg-slate-50/50">
                  {allowanceHeads.map((h) => (
                    <div key={h._id} className="flex items-center gap-2">
                      <span className="text-sm font-medium shrink-0 w-28">{h.name} ({h.code})</span>
                      <Input
                        type="number"
                        min={0}
                        className="h-8"
                        value={form.allowanceEntries[h._id] ?? ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            allowanceEntries: { ...f.allowanceEntries, [h._id]: e.target.value },
                          }))
                        }
                        placeholder={h.calculationType === "percentage" ? `${h.value}% of basic` : `Default ₹${h.value}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Allowances (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.allowances}
                  onChange={(e) => setForm((f) => ({ ...f, allowances: e.target.value }))}
                  placeholder="0"
                />
              </div>
            )}
            {deductionHeads.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-[#0263cc]">Deductions (from created heads)</Label>
                <p className="text-xs text-slate-500">Leave amount blank to use head default.</p>
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2 bg-slate-50/50">
                  {deductionHeads.map((h) => (
                    <div key={h._id} className="flex items-center gap-2">
                      <span className="text-sm font-medium shrink-0 w-28">{h.name} ({h.code})</span>
                      <Input
                        type="number"
                        min={0}
                        className="h-8"
                        value={form.deductionEntries[h._id] ?? ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            deductionEntries: { ...f.deductionEntries, [h._id]: e.target.value },
                          }))
                        }
                        placeholder={h.calculationType === "percentage" ? `${h.value}% of basic` : `Default ₹${h.value}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Fixed Deductions (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.fixedDeductions}
                  onChange={(e) => setForm((f) => ({ ...f, fixedDeductions: e.target.value }))}
                  placeholder="0"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Remarks (optional)</Label>
              <Input
                value={form.remarks}
                onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitLoading} className="bg-[#0263cc] hover:bg-[#0255b0] text-white">
              {submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Salary Config</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this salary config? Payroll slips already generated will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitLoading}>
              {submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
